import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useAppSelector } from '../../redux/store';

// Firebase
import { auth, functions } from '../../firebaseConfig'; // Adjust the path based on your project structure
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, documentId, Timestamp, limit, doc, getDoc, collectionGroup, addDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import getCurrentScreen from '../../Helper/GetScreen';

// functions
import { whatsappTemplateMessage, sendWhatsAppTemplate } from "../../pages/Clients/Admin/Function/WhatsappBusinessCloudClient";
import { sendGmailEmail, EmailRecipient, EmailAttachment } from '../../utils/gmailUtils';
import { Calendar, Mail, Search } from 'lucide-react';
import GradientSubmitButton from '../GradientSubmitButton';

interface UserData {
    id: string;
    name: string;
    email: string;
    phone: string;
    staffRoleId?: string;
    image?: string;
    isActive?: boolean;
    isAdmin?: boolean;
    isManager?: boolean;
    isStaff?: boolean;
    managerId?: string;
}

interface ActionModalsProps {
    action: string;
    selectAll?: boolean;
    count: number;
    selectedIds: string[];
    neglectedIds?: string[];
    filterQuery?: {
        sourceId?: string;
        stateId?: string;
        stageId?: string;
        statusId?: string;
        admissionStatusId?: string;
        welcomeCallByAdmissionTeamStatusId?: string;
        visaFileProcessingStatusId?: string;
        applicationMovedStatusId?: string;
        conditionalOfferLetterStatusId?: string;
        unconditionalOfferLetterStatusId?: string;
        feePaymentStatusId?: string;
        fileSubmissionStatusId?: string;
        hasActiveFollowUp?: boolean | null | undefined;
        assignedTo?: string[];
        campaignId?: string;
        startDate?: Timestamp | null | undefined;
        endDate?: Timestamp | null | undefined;
        nameSearchQuery?: string | null;
        phoneSearchQuery?: string | null;
    };
    onClose: (message: string) => void;
}

interface Settings {
    id: string;
    name: string;
    parentId?: string; // Optional for sub-settings
}

// Simple Toast Component
interface ToastProps {
    message: string;
    type: 'success' | 'error' | 'info';
    onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 20000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const getToastColor = () => {
        switch (type) {
            case 'success':
                return 'bg-green-500';
            case 'error':
                return 'bg-red-500';
            case 'info':
                return 'bg-blue-500';
            default:
                return 'bg-gray-500';
        }
    };

    return (
        <div className={`fixed bottom-4 right-4 ${getToastColor()} text-white px-4 py-3 rounded-md shadow-lg z-50 flex items-center`}>
            <div className="mr-3">{message}</div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
                ×
            </button>
        </div>
    );
};

// Define the phone number document type based on the example provided
interface PhoneNumber {
    assignedUsers: string[];
    companyId: string;
    displayPhoneNumber: string;
    numberId: string;
    qualityRating: string;
    status: string;
    updatedAt: any; // Firestore timestamp
    verifiedName: string;
}

// Define message types
interface WhatsAppMessage {
    id: string;
    from: string;
    to?: string; // Add 'to' field to fix error
    type: 'text' | 'image' | 'video' | 'document' | 'audio' | 'location' | 'template';
    timestamp: string;

    // For text messages
    text?: {
        body: string;
    };

    // For image messages
    image?: {
        id: string;
        mime_type: string;
        sha256: string;
        caption?: string;
        url?: string;
    };

    // For video messages
    video?: {
        id: string;
        mime_type: string;
        sha256: string;
        caption?: string;
        url?: string;
    };

    // For document messages
    document?: {
        id: string;
        mime_type: string;
        sha256: string;
        filename: string;
        url?: string;
        caption?: string; // Add caption field
    };

    // For location messages
    location?: {
        latitude: number;
        longitude: number;
        name?: string;
        address?: string;
    };

    // For audio messages
    audio?: {
        id: string;
        mime_type: string;
        sha256: string;
        url?: string;
    };

    // For template messages
    header?: string;
    body?: string;
    footer?: string;

    // Used for messages sent from the client
    message?: string;
    direction?: 'incoming' | 'outgoing';
    status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
    mediaUrl?: string;
    contactName?: string;
}

// Define WhatsApp access token interface
interface WhatsAppAccessToken {
    accountAccessToken: string;
    accountExpiration: any; // Firestore timestamp
    accountId: string;
    accountName: string;
    companyId: string;
    updatedAt: any; // Firestore timestamp
}

// Template types based on WhatsApp Business API
type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
type TemplateStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
type TemplateType = 'TEXT' | 'MEDIA' | 'INTERACTIVE';
type ComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';

// Template component interface
interface TemplateComponent {
    type: ComponentType;
    text?: string;
    format?: string;
    example?: {
        header_text?: string[];
        body_text?: string[][];
        header_handle?: string[];
    };
    uploadedFile?: File;
    uploadedFileUrl?: string;
    isUploading?: boolean;
    uploadError?: string;
    uploadedHandle?: string;
}

// Template interface
interface WhatsappTemplate {
    id?: string;
    name: string;
    category: TemplateCategory;
    language: string;
    status?: TemplateStatus;
    components: TemplateComponent[];
    parameter_format?: string;
    createdAt?: any;
    updatedAt?: any;
}

interface EmailTemplate {
    id?: string;
    name: string;
    subject: string;
    content: string;
    category: string;
    createdAt: Date;
    lastModified: Date;
}

const ActionModals: React.FC<ActionModalsProps> = ({ action, count, selectedIds, onClose }) => {

    const navigate = useNavigate();

    // Https Callable Functions
    const assignLeads = httpsCallable(functions, 'changeAssignedTo');
    const addSubAgent = httpsCallable(functions, 'addSubAgentIdToLeads');
    const addFollowUp = httpsCallable(functions, 'addFollowUps');
    const updateActive = httpsCallable(functions, 'updateLeadsActive');
    const updateStage = httpsCallable(functions, 'updateLeadsStage');
    const updateSource = httpsCallable(functions, 'updateLeadsSource');
    const updateState = httpsCallable(functions, 'updateLeadsState');
    const updateStatus = httpsCallable(functions, 'updateLeadsStatus');
    const updateProgramType = httpsCallable(functions, 'updateLeadsProgramType');
    const updateIntake = httpsCallable(functions, 'updateLeadsIntake');
    const updateStream = httpsCallable(functions, 'updateLeadsPreferredStream');
    const updateDestination = httpsCallable(functions, 'updateLeadsDestination');
    const permanentDeleteLeads = httpsCallable(functions, 'permanentDeleteLeads');
    const recycleLeads = httpsCallable(functions, 'recycleLeads');
    const uploadWhatsAppMediaFunction = httpsCallable(functions, 'uploadWhatsAppMedia');
    const sendBulkWhatsAppTemplateMessage = httpsCallable(functions, 'sendBulkWhatsAppTemplateMessage');

    const removeSubAgent = httpsCallable(functions, 'removeSubAgentIdFromLeads');

    // Auth Context
    const { currentUser, userData, isAdmin, isManager, isStaff } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    // Loading states
    const [isUpdating, setIsUpdating] = React.useState(false);

    // user data
    const [usersData, setUsersData] = React.useState<UserData[]>([]);

    // stage data
    const [stagesData, setStagesData] = React.useState<Settings[]>([]);
    const [subStagesData, setSubStagesData] = React.useState<Settings[]>([]);
    const [subStatusesData, setSubStatusesData] = React.useState<Settings[]>([]);

    // source data
    const [sourcesData, setSourcesData] = React.useState<Settings[]>([]);
    const [subSourcesData, setSubSourcesData] = React.useState<Settings[]>([]);

    // status data
    const [statusesData, setStatusesData] = React.useState<Settings[]>([]);
    // state data
    const [statesData, setStatesData] = React.useState<Settings[]>([]);

    // program types
    const [programTypes, setProgramTypes] = React.useState<Settings[]>([]);

    // stream data
    const [streamsData, setStreamsData] = React.useState<Settings[]>([]);

    // country data
    const [countries, setCountries] = React.useState<{ id: string, name: string }[]>([]);
    const [countrySearchTerm, setCountrySearchTerm] = React.useState('');
    const [selectedCountryName, setSelectedCountryName] = React.useState('');
    const [isLoadingCountry, setIsLoadingCountry] = React.useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = React.useState(false);

    // current sub-agent
    const [currentSubAgents, setCurrentSubAgents] = useState<{ [leadId: string]: string }>({});
    const [currentSubAgentNames, setCurrentSubAgentNames] = useState<{ [subAgentId: string]: string }>({});
    const [hasSubAgents, setHasSubAgents] = useState(false);


    // payload states for form data
    const [formData, setFormData] = React.useState({
        assignedTo: '',
        transferredAt: null as boolean | null,
        subAgentId: '',
        stateId: '',
        stageId: '',
        subStageId: '',
        sourceId: '',
        subSourceId: '',
        statusId: '',
        subStatusId: '',
        followUp: '',
        date: '',
        programTypeId: '',
        intakeMonth: '',
        intakeYear: '',
        preferredStreamId: '',
        destinationId: '',
        removeSubAgent: false,
    });

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const years = Array.from(
        { length: 11 },
        (_, i) => 2020 + i
    );

    // Function to handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };


    // Function to fetch all users
    const fetchUsers = async () => {
        try {
            // Fetch assignee options
            let usersQuery = query(
                collection(db, "users"),
                where("isActive", "==", true),
                where("companyId", "==", userData?.companyId)
            );

            if (isAdmin && currentScreen === 'Admin') {
                usersQuery = query(
                    collection(db, "users"),
                    where("isActive", "==", true),
                    where("companyId", "==", userData?.companyId),
                );
            } else if (isManager && currentScreen === 'Manager') {
                usersQuery = query(
                    collection(db, "users"),
                    where("isActive", "==", true),
                    where("companyId", "==", userData?.companyId),
                );
            } else if (isStaff && currentScreen === 'Staff') {
                // For staff screen, show all staff members instead of just current user
                usersQuery = query(
                    collection(db, "users"),
                    where("isActive", "==", true),
                    where("companyId", "==", userData?.companyId)
                );
            }

            const snapshot = await getDocs(usersQuery);
            const users: UserData[] = [];
            snapshot.forEach((doc) => {
                users.push({ id: doc.id, ...doc.data() } as UserData);
            });
            setUsersData(users);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }

    // function to fetch all subagents
    const fetchSubAgents = async () => {
        try {

            // Fetch subagents
            const subAgentsQuery = query(
                collection(db, `companies/${userData?.companyId}/subAgents`),
                where("isActive", "==", true)
            );

            const snapshot = await getDocs(subAgentsQuery);
            const subAgents: UserData[] = [];
            snapshot.forEach((doc) => {
                subAgents.push({ id: doc.id, ...doc.data() } as UserData);
            });
            setUsersData(subAgents);
        } catch (error) {
            console.error('Error fetching subagents:', error);
        }
    }

    useEffect(() => {
        if (action === 'Change Affiliate Partner') {
            fetchSubAgents();
        } else {
            fetchUsers();
        }
    }, []);

    const fetchSettings = async (action: string) => {
        try {
            if (action === 'Change Stage') {
                const stageRef = collection(db, `companies/${userData?.companyId}/leadStages`);
                const stageSnapshot = await getDocs(stageRef);

                const subStageRef = collection(db, `companies/${userData?.companyId}/leadSubStages`);
                const subStageSnapshot = await getDocs(subStageRef);

                // store the data in the state
                const stages: Settings[] = [];
                const subStages: Settings[] = [];

                stageSnapshot.forEach((doc) => {
                    stages.push({ id: doc.id, name: doc.data().stage });
                });
                stages.sort((a, b) => a.name.localeCompare(b.name));

                subStageSnapshot.forEach((doc) => {
                    subStages.push({ id: doc.id, name: doc.data().subStage, parentId: doc.data().stageId });
                });
                console.log('Stages:', stages);
                setStagesData(stages);
                console.log('Sub Stages:', subStages);
                setSubStagesData(subStages);
            } else if (action === 'Change Source') {
                const sourceRef = collection(db, `companies/${userData?.companyId}/leadSources`);
                const sourceSnapshot = await getDocs(sourceRef);
                // store the data in the state
                const sources: Settings[] = [];
                sourceSnapshot.forEach((doc) => {
                    sources.push({ id: doc.id, name: doc.data().source });
                });
                sources.sort((a, b) => a.name.localeCompare(b.name));
                console.log('Sources:', sources);
                setSourcesData(sources);

                // Fetch sub-sources if available
                const subSourceRef = collection(db, `companies/${userData?.companyId}/leadSubSources`);
                const subSourceSnapshot = await getDocs(subSourceRef);
                const subSources: Settings[] = [];
                subSourceSnapshot.forEach((doc) => {
                    subSources.push({ id: doc.id, name: doc.data().subSource, parentId: doc.data().sourceId });
                });
                console.log('Sub Sources:', subSources);
                setSubSourcesData(subSources);

            } else if (action === 'Change Status') {
                const statusRef = collection(db, `companies/${userData?.companyId}/leadStatuses`);
                const statusSnapshot = await getDocs(statusRef);
                // store the data in the state
                const statuses: Settings[] = [];
                statusSnapshot.forEach((doc) => {
                    statuses.push({ id: doc.id, name: doc.data().status });
                });
                statuses.sort((a, b) => a.name.localeCompare(b.name));
                console.log('Statuses:', statuses);
                setStatusesData(statuses);

                // Fetch sub-statuses if available
                // Fetch subStatuses
                const subStatusRef = collection(db, `companies/${userData?.companyId}/leadSubStatuses`);
                const subStatusSnapshot = await getDocs(subStatusRef);
                const subStatuses: Settings[] = [];
                subStatusSnapshot.forEach((doc) => {
                    subStatuses.push({
                        id: doc.id,
                        name: doc.data().subStatus,
                        parentId: doc.data().statusId, // parent reference
                    });
                });
                console.log('Sub Statuses:', subStatuses);
                setSubStatusesData(subStatuses);
            } else if (action === 'Change State') {
                const stateRef = collection(db, `companies/${userData?.companyId}/leadStates`);
                const stateSnapshot = await getDocs(stateRef);
                // store the data in the state
                const states: Settings[] = [];
                stateSnapshot.forEach((doc) => {
                    states.push({ id: doc.id, name: doc.data().state });
                });
                states.sort((a, b) => a.name.localeCompare(b.name));
                console.log('States:', states);
                setStatesData(states);
            }

        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    }

    useEffect(() => {
        if (action === 'Change Stage') {
            fetchSettings('Change Stage');
        } else if (action === 'Change Source') {
            fetchSettings('Change Source');
        } else if (action === 'Change Status') {
            fetchSettings('Change Status');
        } else if (action === 'Change State') {
            fetchSettings('Change State');
        }
    }, []);

    const leadAttributes = async (action: string) => {
        try {
            if (action === 'Change ProgramType') {
                const programTypeRef = collection(db, `companies/${userData?.companyId}/programTypes`);
                const programTypeSnapshot = await getDocs(programTypeRef);
                // store the data in the state
                const programTypes: Settings[] = [];
                programTypeSnapshot.forEach((doc) => {
                    programTypes.push({ id: doc.id, name: doc.data().programType });
                });
                setProgramTypes(programTypes);
            } else if (action === 'Change Stream') {
                const streamRef = collection(db, `companies/${userData?.companyId}/preferredStreams`);
                const streamSnapshot = await getDocs(streamRef);
                // store the data in the state
                const streams: Settings[] = [];
                streamSnapshot.forEach((doc) => {
                    streams.push({ id: doc.id, name: doc.data().preferredStream });
                });
                console.log('Streams:', streams);
                setStreamsData(streams);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    }

    useEffect(() => {
        if (action === 'Change ProgramType') {
            leadAttributes('Change ProgramType');
        } else if (action === 'Change Stream') {
            leadAttributes('Change Stream');
        }
    }, []);

    const handleCountrySearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setSelectedCountryName('');
        formData.destinationId = '';
        setCountrySearchTerm(e.target.value);

        if (value.length < 3) {
            setCountries([]);
            return;
        }

        setIsLoadingCountry(true);
        try {
            const countriesRef = collection(db, 'countries');
            const q = query(
                countriesRef,
                where('name', '>=', value),
                where('name', '<=', value + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const countriesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setCountries(countriesData);
        } catch (error) {
            console.error("Error searching countries:", error);
        } finally {
            setIsLoadingCountry(false);
        }
    }

    // Whats app template functions

    // File to base64 conversion utility
    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Extract the base64 part from the data URL
                const base64String = reader.result as string;
                // Remove the "data:mimetype;base64," prefix
                const base64Content = base64String.split(',')[1];
                resolve(base64Content);
            };
            reader.onerror = error => reject(error);
        });
    };

    // Define TypeScript interfaces
    interface Variable {
        fullVar: string;  // With curly braces e.g. {{name}}
        key: string;      // Without curly braces e.g. name
    }

    interface ComponentState {
        type: string;
        text: string;
        variables: Variable[];
        inputFieldCount: number;
    }

    interface InputValues {
        [key: string]: string;
    }

    // State variables
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
    const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedMediaType, setSelectedMediaType] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadedMediaId, setUploadedMediaId] = useState<string | null>(null);
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [whatsappAccessToken, setWhatsappAccessToken] = useState<WhatsAppAccessToken | null>(null);
    const [expired, setExpired] = useState(false);
    const [selectedContact, setSelectedContact] = useState<string | null>(null);
    const [selectedPhoneNumberInfo, setSelectedPhoneNumberInfo] = useState<any>(null);
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [mediaFormat, setMediaFormat] = useState<string | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<WhatsappTemplate>({} as WhatsappTemplate);
    const [sendLoading, setSendLoading] = useState(false);
    // Generate the state based on components
    const [componentsState, setComponentsState] = useState<ComponentState[]>([]);

    // State to track input values
    const [inputValues, setInputValues] = useState<InputValues>({});

    // Initialize app when component mounts
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                // Get the current logged-in user
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    setError("No user logged in");
                    setLoading(false);
                    return;
                }

                // First, get the user's company ID from their user document
                const userDocRef = doc(db, "users", currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (!userDocSnap.exists()) {
                    setError("User document not found");
                    setLoading(false);
                    return;
                }

                const userData = userDocSnap.data();
                const userCompanyId = userData.companyId;

                if (!userCompanyId) {
                    setError("User is not associated with a company");
                    setLoading(false);
                    return;
                }

                // Store the company ID in the component's state
                setCompanyId(userCompanyId);

                // Query the phoneNumbers collection group for documents with the matching companyId
                // AND where the current user is in the assignedUsers array
                const phoneNumbersQuery = query(
                    collectionGroup(db, "phoneNumbers"),
                    where("companyId", "==", userCompanyId),
                    where("assignedUsers", "array-contains", currentUser.uid)
                );

                const querySnapshot = await getDocs(phoneNumbersQuery);

                const phonesData: PhoneNumber[] = [];
                querySnapshot.forEach((doc) => {
                    phonesData.push(doc.data() as PhoneNumber);
                });

                setPhoneNumbers(phonesData);

                setLoading(false);
            } catch (err) {
                console.error("Error fetching initial data:", err);
                setError("Error fetching data");
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    // this useEffect to fetch current sub-agent data
    useEffect(() => {
        const fetchCurrentSubAgents = async () => {
            if (action === 'Change Affiliate Partner' && selectedIds.length > 0) {
                try {
                    const subAgentsMap: { [leadId: string]: string } = {};
                    const subAgentNamesMap: { [subAgentId: string]: string } = {};
                    let hasAnySubAgent = false;

                    // Fetch current sub-agent IDs from leads
                    for (const leadId of selectedIds) {
                        const leadDocRef = doc(db, `companies/${userData?.companyId}/leads`, leadId);
                        const leadDoc = await getDoc(leadDocRef);

                        if (leadDoc.exists()) {
                            const leadData = leadDoc.data();
                            if (leadData.subAgentId) {
                                subAgentsMap[leadId] = leadData.subAgentId;
                                hasAnySubAgent = true;

                                // If we haven't fetched this sub-agent's name yet, fetch it
                                if (!subAgentNamesMap[leadData.subAgentId]) {
                                    const subAgentDocRef = doc(db, `companies/${userData?.companyId}/subAgents`, leadData.subAgentId);
                                    const subAgentDoc = await getDoc(subAgentDocRef);

                                    if (subAgentDoc.exists()) {
                                        subAgentNamesMap[leadData.subAgentId] = subAgentDoc.data().name || 'Unknown';
                                    } else {
                                        subAgentNamesMap[leadData.subAgentId] = 'Unknown Sub-Agent';
                                    }
                                }
                            }
                        }
                    }

                    setCurrentSubAgents(subAgentsMap);
                    setCurrentSubAgentNames(subAgentNamesMap);
                    setHasSubAgents(hasAnySubAgent);
                } catch (error) {
                    console.error('Error fetching current sub-agents:', error);
                    setHasSubAgents(false);
                }
            }
        };

        fetchCurrentSubAgents();
    }, [action, selectedIds, userData?.companyId]);

    // this helper function to display the current sub-agents with lead counts
    const getCurrentSubAgentsDisplay = () => {
        const subAgentCounts: { [subAgentId: string]: number } = {};

        // Count how many leads each sub-agent has
        Object.values(currentSubAgents).forEach(subAgentId => {
            subAgentCounts[subAgentId] = (subAgentCounts[subAgentId] || 0) + 1;
        });

        if (Object.keys(subAgentCounts).length === 0) {
            return "No Affiliate Partner assigned";
        }

        return Object.entries(subAgentCounts)
            .map(([subAgentId, count]) => {
                const name = currentSubAgentNames[subAgentId] || 'Unknown Sub-Agent';
                return `${name} (${count} lead${count > 1 ? 's' : ''})`;
            })
            .join(', ');
    };

    // Handle phone number selection
    const handlePhoneNumberSelect = (phoneNumberId: string) => {
        const selectedPhoneNumber = phoneNumbers.find(phone => phone.numberId === phoneNumberId);
        if (selectedPhoneNumber) {
            setSelectedPhoneNumberInfo(selectedPhoneNumber);
        }
    };

    // Handle input change
    const handleVariablesInputChange = (type: string, key: string, value: string): void => {
        setInputValues(prev => ({
            ...prev,
            [`${type}_${key}`]: value
        }));
    };

    // Fetch WhatsApp access token
    const fetchAccessToken = async () => {
        if (!companyId) return null;

        try {
            // Query the whatsappBusinessCloudAccounts collection for this company
            const accessTokenQuery = query(
                collection(db, "whatsappBusinessCloudAccounts"),
                where("companyId", "==", companyId)
            );

            const querySnapshot = await getDocs(accessTokenQuery);

            if (querySnapshot.empty) {
                console.error("No WhatsApp access token found for this company");
                return null;
            }

            // Get the first matching document
            const tokenData = querySnapshot.docs[0].data() as WhatsAppAccessToken;

            setWhatsappAccessToken(tokenData);
            console.log("WhatsApp access token set:", tokenData);

            // Check if token is expired
            const expiration = tokenData.accountExpiration?.toDate ?
                tokenData.accountExpiration.toDate() :
                new Date(tokenData.accountExpiration);

            if (expiration < new Date()) {
                console.error("WhatsApp access token has expired");
                setExpired(true);
                return null;
            }

            // Set the token in state
            setAccessToken(tokenData.accountAccessToken);
            return tokenData.accountAccessToken;

        } catch (err) {
            console.error("Error fetching WhatsApp access token:", err);
            return null;
        }
    };

    const handleTemplateClick = async () => {

        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("User not authenticated");
                return;
            }
            const userRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
                console.error("User document not found");
                return;
            }
            const userData = userDoc.data();
            const companyId = userData.companyId;
            if (!companyId) {
                console.error("Company ID not found in user document");
                return;
            }

            // Fetch message templates 
            const reponse = await whatsappTemplateMessage(companyId);

            if (reponse) {
                const templates = reponse.data;
                console.log("Templates:", templates);
                setTemplates(templates);
                setShowTemplateModal(true);
            } else {
                console.error("No templates found");
            }

        } catch (error) {
            console.error('Error fetching message templates:', error);
            throw error;
        }
    }

    useEffect(() => {
        if (action == 'Sent Whatsapp Template') {
            handleTemplateClick();
        }
    }, [action]);

    // Get status badge color based on template status
    const getStatusColor = (status?: TemplateStatus) => {
        switch (status) {
            case 'APPROVED':
                return 'bg-green-100 text-green-800';
            case 'REJECTED':
                return 'bg-red-100 text-red-800';
            case 'PENDING':
            default:
                return 'bg-yellow-100 text-yellow-800';
        }
    };

    const handleCloseTemplateModal = () => {
        setShowTemplateModal(false);
        setTemplates([]);
    };

    // Handle file selection for media messages
    const handleTemplateFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
        // Check if a file was selected
        const file = e.target.files?.[0];
        if (!file || !selectedPhoneNumberInfo || !companyId) {
            // No file selected or missing required data
            console.log("File selection canceled or missing data");
            setSelectedMediaType(null);
            return;
        }

        // Store the selected file for later sending
        setSelectedFile(file);

        let mediaType: string | null = null;

        // find the media type based on the file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png') {
            mediaType = 'image';
        } else if (fileExtension === 'mp4' || fileExtension === 'mov') {
            mediaType = 'video';
        } else if (fileExtension === 'pdf' || fileExtension === 'docx' || fileExtension === 'txt') {
            mediaType = 'document';
        } else {
            console.error("Unsupported file type");
            setToast({
                show: true,
                message: "Unsupported file type. Please select an image, video, or document.",
                type: "error"
            });
            return;
        }

        try {
            console.log("Uploading media file:", file);
            console.log("-------------------------------------------");


            setUploadingMedia(true);

            // Get access token if not already available
            const token = accessToken || await fetchAccessToken();

            if (!token) {
                throw new Error("Could not get access token for WhatsApp API");
            }

            // Convert file to base64
            // Remove the data URL prefix (e.g., 'data:image/jpeg;base64,')
            const base64Content = await fileToBase64(file);

            // Upload media to WhatsApp
            const uploadResult = await uploadWhatsAppMediaFunction({
                accessToken: token,
                accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
                mediaType: mediaType,
                mediaData: base64Content,
                mimeType: file.type,
                filename: file.name
            });

            const { success, mediaId } = uploadResult.data as {
                success: boolean;
                mediaId: string;
            };

            if (!success || !mediaId) {
                throw new Error("Failed to upload media to WhatsApp");
            }

            // Store the media ID for later sending
            setUploadedMediaId(mediaId);
            setUploadingMedia(false);

            // Show a toast indicating media is ready to send
            setToast({
                show: true,
                message: `${selectedMediaType} uploaded successfully. Add a caption if you wish, then click send.`,
                type: "success"
            });

        } catch (err) {
            console.error("Error uploading media:", err);
            setUploadingMedia(false);
            setSelectedMediaType(null);
            setSelectedFile(null);
            setUploadedMediaId(null);

            // Show error toast
            setToast({
                show: true,
                message: "Failed to upload media. Please try again.",
                type: "error"
            });
        }
    };

    const mediaAcceptedFormats = (format: string | null) => {
        switch (format) {
            case 'IMAGE':
                return "image/jpeg, image/png, image/jpg"
            case 'VIDEO':
                return "video/mp4, video/mov"
            case 'DOCUMENT':
                return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            default:
                return undefined;
        }
    }

    // Function to process components and extract variables
    function processComponents(components: TemplateComponent[]): ComponentState[] {
        const state: ComponentState[] = [];
        const variableRegex = /{{([^{}]+)}}/g;

        components.forEach(component => {
            if (!component.text) {
                return;
            }

            const variables: Variable[] = [];
            let match: RegExpExecArray | null;
            let textCopy = component.text;

            while ((match = variableRegex.exec(textCopy)) !== null) {
                variables.push({
                    fullVar: match[0],
                    key: match[1]
                });
            }

            if (variables.length > 0) {
                state.push({
                    type: component.type,
                    text: component.text,
                    variables: variables,
                    inputFieldCount: variables.length
                });
            }
        });

        return state;
    }

    const templateFormat = (template: WhatsappTemplate) => {
        console.log("Template format:", template);
        if (template.components[0].format === 'IMAGE' || template.components[0].format === 'VIDEO' || template.components[0].format === 'DOCUMENT') {
            if (selectedFile === null) {
                setShowMediaModal(true);
                setMediaFormat(template.components[0].format);
            }
        }
        // Set selected template
        setSelectedTemplate(template);
        // Process components to extract variables
        setComponentsState(processComponents(template.components));
    }

    useEffect(() => {
        if (componentsState.length === 0 && showMediaModal === false) {
            // handleSendTemplate(selectedTemplate);
        }
    }, [selectedTemplate, componentsState]);

    const handleComponentModalClose = () => {
        setComponentsState([]);
        setInputValues({});
        setSelectedTemplate({} as WhatsappTemplate);
        setSelectedPhoneNumberInfo(null);
        setShowMediaModal(false);
        setSelectedFile(null);
        setSendLoading(false);
    }

    // Email sending and template functions

    // States
    const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
    const [recipientEmailWithId, setRecipientEmailWithId] = useState<{ [key: string]: string }[]>([]);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');

    const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [emailSendStatus, setEmailSendStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    // Function to find the email of each selected ID from leads subcollection in companies collection
    useEffect(() => {
        if (action === 'Send Email') {
            const fetchEmails = async () => {
                try {
                    const emails: string[] = [];
                    const noEmailIds: string[] = [];
                    const recipientEmailWithId: { [key: string]: string }[] = [];

                    for (const id of selectedIds) {
                        const docRef = doc(db, `companies/${userData?.companyId}/leads`, id);
                        const docSnap = await getDoc(docRef);
                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            if (data && data.email) {
                                emails.push(data.email);
                                recipientEmailWithId.push({ [id]: data.email });
                            } else {
                                noEmailIds.push(id);
                            }
                        }
                    }

                    console.log('Fetched Emails:', emails);
                    const uniqueEmails = Array.from(new Set(emails));
                    const emailRecipients: EmailRecipient[] = uniqueEmails.map(email => ({
                        email
                    }));

                    setRecipients(emailRecipients);
                    setRecipientEmailWithId(recipientEmailWithId);

                    // Show toast if some leads don't have emails
                    if (noEmailIds.length > 0) {
                        setToast({
                            show: true,
                            message: `${noEmailIds.length} selected lead(s) don't have email addresses and won't receive emails`,
                            type: "info"
                        });
                    }

                    // Show warning if no emails found at all
                    if (emails.length === 0) {
                        setToast({
                            show: true,
                            message: "None of the selected leads have email addresses",
                            type: "error"
                        });
                        // Close the email section if no emails found
                        // onClose('');
                    }

                } catch (error) {
                    console.error('Error fetching emails:', error);
                    setToast({
                        show: true,
                        message: "Error fetching lead emails",
                        type: "error"
                    });
                    // Close the email section on error
                    onClose('');
                }
            };
            fetchEmails();
        }
    }, [action, selectedIds]);

    // Add this new useEffect to handle the email sending completion
    useEffect(() => {
        if (emailSendStatus === 'success') {
            setToast({
                show: true,
                message: "Email sent successfully!",
                type: "success"
            });
            // Reset email send status and close the email section
            setEmailSendStatus('idle');
            onClose('');
        } else if (emailSendStatus === 'error') {
            setToast({
                show: true,
                message: "Failed to send email",
                type: "error"
            });
            // Reset email send status (but keep email section open so user can retry)
            setEmailSendStatus('idle');
        }
    }, [emailSendStatus]);


    const handleIconClick = () => {
        // Trigger the hidden file input when icon is clicked
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;

        if (files && files.length > 0) {
            setUploading(true);
            try {
                // Convert all files in parallel and collect results
                const newAttachments: EmailAttachment[] = await Promise.all(
                    Array.from(files).map(async (file) => {
                        // Optional size check (10MB example limit)
                        if (file.size > 10 * 1024 * 1024) {
                            setToast({
                                show: true,
                                message: `File ${file.name} exceeds the 10MB size limit`,
                                type: "error"
                            });
                            throw new Error(`File ${file.name} exceeds the 10MB size limit`);
                        }

                        // Optional file type check
                        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                        if (!allowedTypes.includes(file.type)) {
                            console.warn(`File type ${file.type} might not be supported`);
                            // Continue anyway or throw error based on your requirements
                        }

                        const base64Content = await fileToBase64(file);
                        return {
                            filename: file.name,
                            content: base64Content,
                            mimeType: file.type,
                        };
                    })
                );

                // Update state once with all new attachments
                setAttachments((prev) => [...prev, ...newAttachments]);

                setToast({
                    show: true,
                    message: `${newAttachments.length} file(s) attached successfully`,
                    type: "success"
                });
            } catch (error: any) {
                console.error("Error processing files:", error);
                setToast({
                    show: true,
                    message: `Error adding attachments: ${error.message}`,
                    type: "error"
                });
            } finally {
                setUploading(false);
            }
        }

        // Reset the file input value
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleAttachmentRemove = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    // Function to handle form submission
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        setIsUpdating(true);

        let payload = {};

        if (action === 'Change Assignee' || action === 'Recycle Leads') {
            payload = {
                assignedTo: formData.assignedTo,
                transferredAt: formData.transferredAt
            };
        } else if (action === 'Change Affiliate Partner') {
            // If removeSubAgent is true, we'll handle it separately
            if (formData.removeSubAgent) {
                // We'll handle the removal case separately
            } else {
                payload = {
                    subAgentId: formData.subAgentId,
                };
            }
        } else if (action === 'Add Follow Up') {
            payload = {
                followUp: formData.followUp,
                date: Timestamp.fromDate(new Date(formData.date)),
            };
        } else if (action === 'Change Stage') {
            payload = {
                stageId: formData.stageId,
                subStageId: subStagesData.find(subStage => subStage.id === formData.subStageId && subStage.parentId === formData.stageId) ? formData.subStageId : null,
            };
        } else if (action === 'Change Source') {
            payload = {
                sourceId: formData.sourceId,
                subSourceId: subSourcesData.find(subSource => subSource.id === formData.subSourceId && subSource.parentId === formData.sourceId) ? formData.subSourceId : null,
            };
        } else if (action === 'Change Status') {
            payload = {
                statusId: formData.statusId,
                subStatusId: subStatusesData.find(subStatus => subStatus.id === formData.subStatusId && subStatus.parentId === formData.statusId) ? formData.subStatusId : null,
            };
        } else if (action === 'Change State') {
            payload = {
                stateId: formData.stateId,
            };
        } else if (action === 'Change ProgramType') {
            payload = {
                programTypeId: formData.programTypeId,
            };
        } else if (action === 'Change Intake') {
            payload = {
                intakeMonth: formData.intakeMonth,
                intakeYear: formData.intakeYear,
            };
        } else if (action === 'Change Stream') {
            payload = {
                preferredStreamId: formData.preferredStreamId,
            };
        } else if (action === 'Change Country') {
            payload = {
                destinationId: formData.destinationId,
            };
        }

        try {
            console.log('Selected IDs:', selectedIds);

            let result;

            if (action === 'Change Assignee') {
                result = await assignLeads({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change Affiliate Partner') {
                if (formData.removeSubAgent) {
                    // Call remove function instead of add
                    result = await removeSubAgent({ selectedIds }) as { data: { success: boolean, message: string } };
                } else {
                    result = await addSubAgent({ selectedIds, payload }) as { data: { success: boolean, message: string } };
                }
            } else if (action === 'Add Follow Up') {
                result = await addFollowUp({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Mark as closed') {
                console.log('Payload not required for Mark as closed');
                result = await updateActive({ selectedIds }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change Stage') {
                result = await updateStage({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change Source') {
                result = await updateSource({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change Status') {
                result = await updateStatus({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change State') {
                result = await updateState({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change ProgramType') {
                result = await updateProgramType({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change Intake') {
                result = await updateIntake({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change Stream') {
                result = await updateStream({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Change Country') {
                result = await updateDestination({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            } else if (action === 'Delete Leads') {
                result = await permanentDeleteLeads({ selectedIds }) as { data: { success: boolean, message: string } };
            } else if (action === 'Recycle Leads') {
                result = await recycleLeads({ selectedIds, payload }) as { data: { success: boolean, message: string } };
            }

            console.log('Result:', result);

            if (result?.data.success) {
                console.log(result.data.message);
                setIsUpdating(false);

                // Custom success message for Change Affiliate Partner action
                let successMessage = '';
                if (action === 'Change Affiliate Partner') {
                    if (formData.removeSubAgent) {
                        successMessage = 'The Affiliate Partner was removed successfully.';
                    } else {
                        successMessage = 'The Affiliate Partner was assigned successfully.';
                    }
                } else {
                    successMessage = result.data.message;
                }

                onClose(successMessage);
            } else {
                // Handle case where result exists but success is false
                console.error('Operation failed:', result?.data.message);
                setIsUpdating(false);
                // You might want to show an error toast here
                setToast({
                    show: true,
                    message: result?.data.message || 'Operation failed',
                    type: "error"
                });
            }

        } catch (error: any) {
            console.error('Error processing leads:', error);
            setIsUpdating(false);

            // Show error toast
            setToast({
                show: true,
                message: error.message || 'An error occurred while processing your request',
                type: "error"
            });
        }
    };

    // Function to handle template submission
    const handleTemplateSubmit = async () => {
        console.log('Selected Template:', selectedTemplate);
        setSendLoading(true);

        const result = await sendWhatsAppTemplate({
            selectedContact: selectedIds,
            selectedPhoneNumberInfo: selectedPhoneNumberInfo,
            companyId: companyId as string,
            template: selectedTemplate,
            inputValues: inputValues,
            componentsState: componentsState,
            uploadedMediaId: uploadedMediaId,
            selectedFile: selectedFile,
            accessToken: accessToken as string,
            fetchAccessToken: fetchAccessToken,
            sendWhatsAppTemplateMessage(params) {
                return sendBulkWhatsAppTemplateMessage(params);
            },
            onSuccess() {
                setToast({
                    show: true,
                    message: "WhatsApp template sent successfully.",
                    type: "success"
                });
            },
            onError(error: any) {
                setToast({
                    show: true,
                    message: `Error sending WhatsApp template: ${error.message}`,
                    type: "error"
                });
            },
            onFinally() {
                onClose('');
                setIsUpdating(false);
                setSendLoading(false);
            }
        });
        console.log('Result:', result);
    }

    // Function to handle send email
    const handleSendEmail = async () => {
        setSendLoading(true);
        try {
            // Check if there are any recipients
            if (recipients.length === 0) {
                throw new Error("Please add at least one recipient");
            }

            // Check if subject is provided
            if (!emailSubject.trim()) {
                throw new Error("Please add a subject");
            }

            // Calculate total attachment size
            const totalSize = attachments.reduce((size, attachment) => {
                // Base64 string length * 0.75 is approximately the decoded size in bytes
                const decodedSize = attachment.content.length * 0.75;
                return size + decodedSize;
            }, 0);

            // Gmail has a 25MB attachment limit
            if (totalSize > 25 * 1024 * 1024) {
                setToast({
                    show: true,
                    message: "Total attachment size exceeds 25MB limit",
                    type: "error"
                });
                throw new Error("Total attachment size exceeds 25MB limit");
            }

            console.log('Sending email with the following details:', {
                recipients,
                subject: emailSubject,
                body: '\n' + emailBody,
                attachments: attachments
            });


            const result = await sendGmailEmail({
                to: recipients,
                subject: emailSubject,
                body: '\n' + emailBody.trim(), // Remove the newline prefix
                attachments: attachments,
                isHtml: false
            });

            console.log('Email sent successfully:>>>>>>>>>>>>>>>', result);

            // if response is true then store the email info in database in emails collection
            if (result) {
                console.log('Storing email in Firestore...');

                for (const recipient of recipientEmailWithId) {
                    const recipientId = Object.keys(recipient)[0];
                    const recipientEmail = recipient[recipientId];
                    const emailData = {
                        subject: emailSubject,
                        to: [recipientEmail],
                        createdAt: Timestamp.now(),
                        userId: currentUser?.uid,
                        leadId: recipientId // Assuming you want to link to the first selected lead
                    }

                    // Store email in Firestore
                    const emailRef = collection(db, `companies/${userData?.companyId}/emails`);
                    await addDoc(emailRef, emailData);
                    console.log('Email stored in Firestore:', emailData);
                }

                // Clear form after successful send and saving the email data to Firestore
                setRecipients([]);
                setEmailSubject('');
                setEmailBody('');
                setAttachments([]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = ''; // Reset file input
                }
                setRecipientEmailWithId([]);
                setToast({
                    show: true,
                    message: "Email sent and stored successfully.",
                    type: "success"
                });
                onClose(''); // Close the modal after sending
            }

            setToast({
                show: true,
                message: "Email sent successfully.",
                type: "success"
            });
        } catch (error: any) {
            console.error('Error sending email:', error);
            setToast({
                show: true,
                message: `Error sending email: ${error.message}`,
                type: "error"
            });
        } finally {
            setSendLoading(false);
        }
    }

    // Templates related state and functions
    const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
    const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    const categories = ['All', 'Onboarding', 'Security', 'Marketing', 'Support', 'General'];

    const filteredTemplates = emailTemplates.filter(template => {
        const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.subject.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    // Fetch templates from Firestore
    useEffect(() => {
        const fetchTemplates = async () => {
            if (!userData) return;
            try {
                const templatesRef = collection(db, `companies/${userData.companyId}/emailTemplates`);
                const templatesQuery = query(templatesRef);
                const querySnapshot = await getDocs(templatesQuery);
                const fetchedTemplates: EmailTemplate[] = [];
                querySnapshot.forEach((doc) => {
                    const data = doc.data() as EmailTemplate;
                    fetchedTemplates.push({
                        id: doc.id,
                        name: data.name,
                        subject: data.subject,
                        content: data.content,
                        category: data.category,
                        createdAt: data.createdAt,
                        lastModified: data.lastModified
                    });
                });
                setEmailTemplates(fetchedTemplates);
            } catch (error) {
                console.error('Error fetching templates:', error);
            }
        };

        fetchTemplates();
    }, [userData]);

    const formatDate = (date: Date | Timestamp) => {

        if (date instanceof Timestamp) {
            date = date.toDate();
        }

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleTemplateSelect = (template: EmailTemplate) => {
        setEmailSubject(template.subject);
        setEmailBody(template.content);
        setIsTemplatesOpen(false); // Close the template modal
    }

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
                <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/change.svg`} alt="Staff Icon" />
                        </div>
                        <h2 className="text-xl font-semibold text-black">{action}</h2>
                    </div>
                    <button
                        onClick={() => onClose('')}
                        className="absolute top-4 right-4 font-semibold text-3xl"
                        style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent' }}
                    >
                        <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
                    </button>
                </div>
                {(action === 'Change Assignee' || action === 'Recycle Leads' || action === 'Change Affiliate Partner') && (
                    <form
                        onSubmit={handleSubmit}
                    >
                        <div className="grid grid-cols-2 gap-6 mt-8">
                            {(action === 'Change Assignee' || action === 'Recycle Leads') && (
                                <>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Select Staff</label>
                                        <select
                                            name="assignedTo"
                                            value={formData.assignedTo}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full p-2 border rounded-md mt-2"
                                            style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                        >
                                            <option value="">Select Staff</option>
                                            {usersData
                                                .filter(user => (user.isStaff || user.isManager) && user.isActive)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map((staff) => (
                                                    <option key={staff.id} value={staff.id}>
                                                        {staff.name}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>

                                    {/* Change transferredAt or not */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Include <span className={`text-xs py-1 px-2 rounded bg-[#FDE6E6] text-[#aa3727] `}>Transferred</span> Tag</label>
                                        <div className="flex w-fit bg-gray-100 p-1 rounded-full mt-2">
                                            <button
                                                name='transferredAt'
                                                value='true'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setFormData({
                                                        ...formData,
                                                        transferredAt: formData.transferredAt === true ? null : true
                                                    });
                                                }}
                                                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${formData.transferredAt === true
                                                    ? 'text-white'
                                                    : 'text-gray-600 hover:text-gray-800'
                                                    }`}
                                                style={{
                                                    background: formData.transferredAt === true
                                                        ? 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)'
                                                        : 'transparent'
                                                }}
                                            >
                                                Yes
                                            </button>
                                            <button
                                                name='transferredAt'
                                                value='false'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setFormData({
                                                        ...formData,
                                                        transferredAt: formData.transferredAt === false ? null : false
                                                    });
                                                }}
                                                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${formData.transferredAt === false
                                                    ? 'text-white'
                                                    : 'text-gray-600 hover:text-gray-800'
                                                    }`}
                                                style={{
                                                    background: formData.transferredAt === false
                                                        ? 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)'
                                                        : 'transparent'
                                                }}
                                            >
                                                No
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                            {action === 'Change Affiliate Partner' && (
                                <div>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                                        <div className="flex w-fit bg-gray-100 p-1 rounded-full">
                                            <button
                                                type="button"
                                                onClick={() => setFormData({
                                                    ...formData,
                                                    removeSubAgent: false,
                                                    subAgentId: ''
                                                })}
                                                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${!formData.removeSubAgent
                                                    ? 'text-white'
                                                    : 'text-gray-600 hover:text-gray-800'
                                                    }`}
                                                style={{
                                                    background: !formData.removeSubAgent
                                                        ? 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)'
                                                        : 'transparent'
                                                }}
                                            >
                                                Assign New Partner
                                            </button>

                                            {/* Only show Remove Partner button if sub-agents exist */}
                                            {hasSubAgents && (
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({
                                                        ...formData,
                                                        removeSubAgent: true,
                                                        subAgentId: ''
                                                    })}
                                                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${formData.removeSubAgent
                                                        ? 'text-white'
                                                        : 'text-gray-600 hover:text-gray-800'
                                                        }`}
                                                    style={{
                                                        background: formData.removeSubAgent
                                                            ? 'linear-gradient(180deg, #FF7B6F 0%, #F9536B 100%)'
                                                            : 'transparent'
                                                    }}
                                                >
                                                    Remove Partner
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {formData.removeSubAgent ? (
                                        <div className="space-y-4">
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                                <div className="flex items-center">
                                                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                                    </svg>
                                                    <span className="text-yellow-800 font-medium">
                                                        This will remove the assigned Affiliate Partner from {count} selected lead(s)
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Display current sub-agent information */}
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                <div className="flex items-center">
                                                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <div>
                                                        <span className="text-blue-800 font-medium">Current Affiliate Partner:</span>
                                                        <p className="text-blue-700 mt-1">{getCurrentSubAgentsDisplay()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Select Affiliate Partner</label>
                                            <select
                                                name="subAgentId"
                                                value={formData.subAgentId}
                                                onChange={handleInputChange}
                                                required={!formData.removeSubAgent}
                                                className="w-full p-2 border rounded-md mt-2"
                                                style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                            >
                                                <option value="">Select Affiliate Partner</option>
                                                {usersData
                                                    .filter(user => user.isActive)
                                                    .map((subAgent) => (
                                                        <option key={subAgent.id} value={subAgent.id}>
                                                            {subAgent.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="col-span-2 mt-8 flex justify-end gap-4">
                                <GradientSubmitButton
                                    type="submit"
                                    loading={isUpdating}
                                    label={
                                        action === 'Change Affiliate Partner'
                                            ? (formData.removeSubAgent ? 'Remove' : 'Assign')
                                            : 'Assign'
                                    }
                                    loadingLabel={
                                        action === 'Change Affiliate Partner'
                                            ? (formData.removeSubAgent ? 'Removing...' : 'Assigning...')
                                            : 'Assigning...'
                                    }
                                    className={`px-8 py-2 text-white rounded-3xl ${action === 'Change Affiliate Partner' && formData.removeSubAgent
                                        ? 'bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]'
                                        : 'bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]'
                                        }`}
                                    disabled={
                                        action === 'Change Affiliate Partner'
                                            ? (formData.removeSubAgent ? false : !formData.subAgentId)
                                            : (formData.transferredAt === null || !formData.assignedTo)
                                    }
                                />

                                <button
                                    type="button"
                                    onClick={() => onClose('')}
                                    className="px-8 py-2 border rounded-3xl hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                )}
                {action === 'Add Follow Up' && (
                    <form
                        onSubmit={handleSubmit}
                    >
                        <div className="grid grid-cols-2 gap-6 mt-8">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Message</label>
                                <input
                                    type="text"
                                    name="followUp"
                                    value={formData.followUp}
                                    onChange={handleInputChange}
                                    placeholder='Enter Message'
                                    className="w-full p-2 border rounded-md mt-2"
                                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Date & Time</label>
                                <input
                                    type="datetime-local"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    placeholder="Select Date & Time"
                                    required
                                    className="w-full p-2 border rounded-md mt-2"
                                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                />
                                {/* button for today and tommorrow */}
                                <div className="flex mt-2 ml-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const today = new Date();
                                            const date = today.toISOString().split('T')[0];
                                            const time = today.toTimeString().split(' ')[0];
                                            setFormData({
                                                ...formData,
                                                date: `${date}T${time}`
                                            });
                                        }}
                                        className="text-xs text-gray-700 bg-teal-200 px-2 py-1 rounded-md mr-2"
                                    >
                                        Today
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const today = new Date();
                                            today.setDate(today.getDate() + 1);
                                            const date = today.toISOString().split('T')[0];
                                            const time = today.toTimeString().split(' ')[0];
                                            setFormData({
                                                ...formData,
                                                date: `${date}T${time}`
                                            });
                                        }}
                                        className="text-xs text-gray-700 bg-cyan-200 px-2 py-1 rounded-md mr-2"
                                    >
                                        Tomorrow
                                    </button>
                                </div>
                            </div>
                            <div className="col-span-2 mt-8 flex justify-end gap-4">
                                <GradientSubmitButton
                                    type="submit"
                                    loading={isUpdating}
                                    label="Update"
                                    loadingLabel="Updating..."
                                    className="px-8 py-2 text-white rounded-3xl bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"
                                />

                                <button
                                    type="button"
                                    onClick={() => onClose('')}
                                    className="px-8 py-2 border rounded-3xl hover:bg-gray-50"
                                >
                                    Cancel
                                </button>

                            </div>
                        </div>
                    </form>
                )}
                {(action === 'Mark as closed' || action === "Delete Leads") && (
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-6 mt-8">
                            {action === 'Mark as closed' && (
                                <div>
                                    <h1>
                                        Are you sure you want to mark the {count > 1 ? count + " " + 'leads' : 'lead'} as closed?
                                    </h1>
                                </div>
                            )}
                            {action === 'Delete Leads' && (
                                <div>
                                    <h1>
                                        Are you sure you want to delete {count > 1 ? count + " " + 'leads' : 'this lead'}?
                                    </h1>
                                </div>
                            )}
                            <div className="col-span-2 mt-8 flex justify-end gap-4">
                                <GradientSubmitButton
                                    type="submit"
                                    loading={isUpdating}
                                    loadingLabel={
                                        action === 'Mark as closed'
                                            ? 'Closing...'
                                            : action === 'Delete Leads'
                                                ? 'Deleting...'
                                                : ''
                                    }
                                    label={
                                        action === 'Mark as closed'
                                            ? 'Mark as Closed'
                                            : action === 'Delete Leads'
                                                ? 'Delete'
                                                : ''
                                    }
                                    className="px-8 py-2 text-white rounded-3xl bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"
                                />
                                <button
                                    type="button"
                                    onClick={() => onClose('')}
                                    className="px-8 py-2 border rounded-3xl hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                )}
                {(action === 'Change Stage' || action === 'Change Source' || action === 'Change Status' || action === 'Change State') && (
                    <form
                        onSubmit={handleSubmit}
                    >
                        <div className="grid grid-cols-2 gap-6 mt-8">
                            {action === 'Change Stage' && (
                                <div className="grid grid-cols-1 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Select Stage</label>
                                        <select
                                            name="stageId"
                                            value={formData.stageId}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full p-2 border rounded-md mt-2"
                                            style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                        >
                                            <option value="">Select Stage</option>
                                            {stagesData.map((stage) => (
                                                <option key={stage.id} value={stage.id}>
                                                    {stage.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {subStagesData.length > 0 && formData.stageId && subStagesData.some(stage => stage.parentId === formData.stageId) && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Select Sub Stage</label>
                                            <select
                                                name="subStageId"
                                                value={formData.subStageId}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full p-2 border rounded-md mt-2"
                                                style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                            >
                                                <option value="">Select Stage</option>
                                                {subStagesData
                                                    .filter(stage => stage.parentId === formData.stageId)
                                                    .map((stage) => (
                                                        <option key={stage.id} value={stage.id}>
                                                            {stage.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                            {action === 'Change Source' && (
                                <div className="grid grid-cols-1 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Select Source</label>
                                        <select
                                            name="sourceId"
                                            value={formData.sourceId}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full p-2 border rounded-md mt-2"
                                            style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                        >
                                            <option value="">Select Source</option>
                                            {sourcesData.map((source) => (
                                                <option key={source.id} value={source.id}>
                                                    {source.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {subSourcesData.length > 0 && formData.sourceId && subSourcesData.some(source => source.parentId === formData.sourceId) && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Select Sub Source</label>
                                            <select
                                                name="subSourceId"
                                                value={formData.subSourceId}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full p-2 border rounded-md mt-2"
                                                style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                            >
                                                <option value="">Select Source</option>
                                                {subSourcesData
                                                    .filter(source => source.parentId === formData.sourceId)
                                                    .map((source) => (
                                                        <option key={source.id} value={source.id}>
                                                            {source.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                            {action === 'Change Status' && (
                                <div className="grid grid-cols-1 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Select Status</label>
                                        <select
                                            name="statusId"
                                            value={formData.statusId}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full p-2 border rounded-md mt-2"
                                            style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                        >
                                            <option value="">Select Status</option>
                                            {statusesData.map((status) => (
                                                <option key={status.id} value={status.id}>
                                                    {status.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {subStatusesData.length > 0 && formData.statusId && subStatusesData.some(status => status.parentId === formData.statusId) && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Select Sub Status</label>
                                            <select
                                                name="subStatusId"
                                                value={formData.subStatusId}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full p-2 border rounded-md mt-2"
                                                style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                            >
                                                <option value="">Select Sub Status</option>
                                                {subStatusesData
                                                    .filter(status => status.parentId === formData.statusId)
                                                    .map((status) => (
                                                        <option key={status.id} value={status.id}>
                                                            {status.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                            {action === 'Change State' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Select State</label>
                                    <select
                                        name="stateId"
                                        value={formData.stateId}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full p-2 border rounded-md mt-2"
                                        style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                    >
                                        <option value="">Select State</option>
                                        {statesData.map((state) => (
                                            <option key={state.id} value={state.id}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="col-span-2 mt-8 flex justify-end gap-4">
                            <GradientSubmitButton
                                type="submit"
                                loading={isUpdating}
                                label="Update"
                                loadingLabel="Updating..."
                                className="px-8 py-2 text-white rounded-3xl bg-gradient-to-b from-[#FF7B6F] to-[#F9536B]"
                            />
                            <button
                                type="button"
                                onClick={() => onClose('')}
                                className="px-8 py-2 border rounded-3xl hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
                {(action === 'Change ProgramType' || action === 'Change Intake' || action === 'Change Stream' || action === 'Change Country') && (
                    <form
                        onSubmit={handleSubmit}
                    >
                        <div className="grid grid-cols-2 gap-6 mt-8">
                            {action === 'Change ProgramType' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Select Program Type</label>
                                    <select
                                        name="programTypeId"
                                        value={formData.programTypeId}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full p-2 border rounded-md mt-2"
                                        style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                    >
                                        <option value="">Select Program Type</option>
                                        {programTypes.map((programType) => (
                                            <option key={programType.id} value={programType.id}>
                                                {programType.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {action === 'Change Intake' && (
                                <div>
                                    <div className="flex">
                                        <div className="relative w-1/2">
                                            <label className="block text-sm font-medium text-gray-700">Select Intake Year</label>
                                            <select
                                                name="intakeYear"
                                                value={formData.intakeYear}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full p-2 border rounded-md mt-2"
                                                style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                            >
                                                <option value="">Select Intake Year</option>
                                                {years.map((year, index) => (
                                                    <option key={index} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="relative w-1/2 ml-4">
                                            <label className="block text-sm font-medium text-gray-700">Select Intake Month</label>
                                            <select
                                                name="intakeMonth"
                                                value={formData.intakeMonth}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full p-2 border rounded-md mt-2"
                                                style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                            >
                                                <option value="">Select Intake Month</option>
                                                {months.map((month, index) => (
                                                    <option key={index} value={month}>
                                                        {month}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {action === 'Change Stream' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Select Preferred Stream</label>
                                    <select
                                        name="preferredStreamId"
                                        value={formData.preferredStreamId}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full p-2 border rounded-md mt-2"
                                        style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                    >
                                        <option value="">Select Preferred Stream</option>
                                        {streamsData.map((stream) => (
                                            <option key={stream.id} value={stream.id}>
                                                {stream.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {action === 'Change Country' && (
                                <div className='relative'>
                                    <label className="block text-sm font-medium text-gray-700">Select Country</label>
                                    <input
                                        type="text"
                                        name="country"
                                        value={countrySearchTerm || selectedCountryName}
                                        onChange={handleCountrySearchChange}
                                        onClick={() => setIsCountryDropdownOpen(true)}
                                        placeholder="Search Country"
                                        className="w-full p-2 border rounded-md mt-2"
                                        style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                    />
                                    {isCountryDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-20 max-h-[300px] overflow-hidden">
                                            <div className="p-2">
                                                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                                    {isLoadingCountry ? (
                                                        <div className="px-4 py-2 text-gray-500">Loading...</div>
                                                    ) : countries.length > 0 ? (
                                                        countries.map((country) => (
                                                            <button
                                                                key={country.id}
                                                                className="px-4 py-2 text-left hover:bg-gray-100 rounded-xl cursor-pointer text-sm w-full"
                                                                onClick={() => {
                                                                    handleInputChange({
                                                                        target: {
                                                                            name: 'destinationId',
                                                                            value: country.id,
                                                                        }
                                                                    } as React.ChangeEvent<HTMLInputElement>);
                                                                    setSelectedCountryName(country.name);
                                                                    setCountrySearchTerm('');
                                                                    setIsCountryDropdownOpen(false);
                                                                }}>
                                                                <div className="flex justify-between items-center text-base">
                                                                    {country.name}
                                                                </div>
                                                            </button>
                                                        ))
                                                    ) : countrySearchTerm.length < 3 ? (
                                                        <div className="px-4 py-2 text-gray-500">Type at least 3 characters to search</div>
                                                    ) : (
                                                        <div className="px-4 py-2 text-gray-500">No countries found</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="col-span-2 mt-8 flex justify-end gap-4">
                            <GradientSubmitButton
                                type="submit"
                                loading={isUpdating}
                                label="Update"
                                loadingLabel="Updating..."
                                className="px-8 py-2 text-white rounded-3xl bg-gradient-to-b from-[#FF7B6F] to-[#F9536B]"
                            />
                            {/*  */}
                            <button
                                type="button"
                                onClick={() => onClose('')}
                                className="px-8 py-2 border rounded-3xl"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
                {action === 'Sent Whatsapp Template' && (
                    <>
                        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
                            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
                                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 1rem)' }}>
                                    <div className="p-6">
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="text-xl font-medium">Templates</h3>
                                            <button
                                                onClick={() => onClose('')}
                                                className="text-gray-500 hover:text-gray-700">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="mb-6">
                                            {templates.length > 0 ? (
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                    {templates.map((template) => (
                                                        <div key={template.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                                                            {/* Card Header */}
                                                            <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 flex justify-between items-center">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="bg-blue-100 rounded-full p-2">
                                                                        <img src={`${process.env.PUBLIC_URL}/images/desktop/template-DBlue.svg`} alt="Template" className="h-6 w-6" />
                                                                    </div>
                                                                    <h3 className="font-semibold text-gray-900 tracking-tight" title={template.name.split('_').join(' ')}>{template.name.split('_').slice(0, 5).join('_') + '...'}</h3>
                                                                </div>
                                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                                                                    {template.status || 'PENDING'}
                                                                </span>
                                                            </div>

                                                            {/* Card Content */}
                                                            <div className="p-5">
                                                                <div className="flex flex-wrap gap-3 mb-4">
                                                                    <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${template.category === 'UTILITY' ? 'bg-blue-100 text-blue-800' : template.category === 'MARKETING' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                                        <span className="inline-flex items-center">
                                                                            {template.category === 'UTILITY' && (
                                                                                <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                                                                </svg>
                                                                            )}
                                                                            {template.category === 'MARKETING' && (
                                                                                <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                                                                </svg>
                                                                            )}
                                                                            {template.category === 'AUTHENTICATION' && (
                                                                                <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                                                </svg>
                                                                            )}
                                                                            {template.category}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                                        <span className="inline-block w-3 h-3 rounded-full bg-indigo-600 mr-1.5 flex-shrink-0"></span>
                                                                        {template.language}
                                                                    </div>
                                                                </div>

                                                                {/* Template Preview */}
                                                                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-5 border border-gray-200 mt-4 shadow-sm">
                                                                    <div className="flex items-center mb-4">
                                                                        <svg className="h-5 w-5 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                        </svg>
                                                                        <h4 className="text-sm font-semibold text-gray-700 tracking-wide">Template Preview</h4>
                                                                    </div>
                                                                    <div className="space-y-3 max-w-xs mx-auto bg-white rounded-lg p-4 border border-gray-300 shadow-md relative overflow-hidden">
                                                                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 via-blue-500 to-purple-500"></div>
                                                                        {template.components?.map((component, index) => {
                                                                            if (component.type === 'HEADER') {
                                                                                if (component.format === 'TEXT' && component.text) {
                                                                                    return (
                                                                                        <div key={index} className="font-medium text-gray-900 pb-2 border-b border-gray-100">{component.text}</div>
                                                                                    );
                                                                                } else if (component.format === 'IMAGE') {
                                                                                    return (
                                                                                        <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 h-40 rounded-lg flex flex-col items-center justify-center border border-blue-100 overflow-hidden">
                                                                                            <svg className="h-12 w-12 text-blue-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h8a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                            </svg>
                                                                                            <span className="text-xs text-blue-500 font-medium">Image</span>
                                                                                        </div>
                                                                                    );
                                                                                } else if (component.format === 'VIDEO') {
                                                                                    return (
                                                                                        <div key={index} className="bg-gradient-to-r from-purple-50 to-pink-50 h-40 rounded-lg flex flex-col items-center justify-center border border-purple-100 overflow-hidden">
                                                                                            <svg className="h-12 w-12 text-purple-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                                            </svg>
                                                                                            <span className="text-xs text-purple-500 font-medium">Video</span>
                                                                                        </div>
                                                                                    );
                                                                                } else if (component.format === 'DOCUMENT') {
                                                                                    return (
                                                                                        <div key={index} className="bg-gradient-to-r from-yellow-50 to-yellow-100 h-40 rounded-lg flex flex-col items-center justify-center border border-yellow-100 overflow-hidden">
                                                                                            <svg className="h-12 w-12 text-yellow-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                                            </svg>
                                                                                            <span className="text-xs text-yellow-500 font-medium">Document</span>
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                            } else if (component.type === 'BODY' && component.text) {
                                                                                return (
                                                                                    <div key={index} className="text-sm text-gray-700 whitespace-pre-line py-3">{component.text}</div>
                                                                                );
                                                                            } else if (component.type === 'FOOTER' && component.text) {
                                                                                return (
                                                                                    <div key={index} className="text-xs text-gray-500 italic pt-2 border-t border-gray-100">{component.text}</div>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })}
                                                                    </div>
                                                                </div>

                                                                {template.createdAt && (
                                                                    <div className="mt-3 text-xs text-gray-500">
                                                                        Created: {template.createdAt.toLocaleString?.() || 'Unknown'}
                                                                    </div>
                                                                )}

                                                                <div className="flex justify-between items-center mt-4">
                                                                    {/* Delete Template Button */}
                                                                    {/* <button
                                                            type="button"
                                                            onClick={() => {
                                                                // setTemplateToDelete(template.name || null);
                                                                // setShowDeleteConfirmation(true);
                                                            }}
                                                            className="mt-4 inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                        >
                                                            <svg className="h-4 w-4 text-red-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                            Delete Template
                                                        </button> */}

                                                                    {/* Send Template Button */}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => templateFormat(template)}
                                                                        className="mt-2 inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                                    >
                                                                        <svg className="h-4 w-4 text-blue-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16
                                                                M4 14h16M4 18h16M4 22h16" />
                                                                        </svg>
                                                                        Send Template
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-8">
                                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                    </svg>
                                                    <p className="mt-2 text-gray-600">No templates found. Create your first template!</p>
                                                    <button
                                                        onClick={() => navigate('/integrations/whatsapp-business-cloud')}
                                                        className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    >
                                                        Create Template
                                                    </button>
                                                </div>
                                            )}

                                            {templates.length > 0 && (
                                                <div className="mt-6 text-right">
                                                    <button
                                                        onClick={() => handleTemplateClick()}
                                                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    >
                                                        <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                        </svg>
                                                        Refresh
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {templates.length > 0 && (
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    onClick={() => handleCloseTemplateModal()}
                                                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {(componentsState?.length > 0 || showMediaModal) && (
                            <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
                                <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
                                    <div className="p-6  overflow-y-auto" style={{ maxHeight: 'calc(90vh - 1rem)' }}>
                                        <div className="flex justify-between items-center mb-6">
                                            <h1 className="text-2xl font-bold">Component Variable Inputs & Media</h1>
                                            <button onClick={() => handleComponentModalClose()} className="text-gray-500 hover:text-gray-700">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        {/* Area to select the phone number. */}
                                        <div className="mb-6">
                                            <label className="block text-sm text-gray-600 mb-2">Select Phone Number</label>
                                            <select
                                                name="phoneNumber"
                                                value={selectedPhoneNumberInfo?.numberId || ''}
                                                onChange={(e) => handlePhoneNumberSelect(e.target.value)}
                                                required
                                                className="w-[30%] p-2 border rounded-md mt-2"
                                                style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                            >
                                                <option value="">Select Phone Number</option>
                                                {phoneNumbers.map((number) => (
                                                    <option key={number.numberId} value={number.numberId}>
                                                        {number.displayPhoneNumber}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {showMediaModal && (
                                            <div className="mb-6">
                                                <label className="block text-sm text-gray-600 mb-2">Upload Media</label>
                                                <input
                                                    type="file"
                                                    accept={mediaAcceptedFormats(mediaFormat)}
                                                    onChange={(e) => handleTemplateFileSelection(e)}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                                                />
                                                {uploadingMedia && (
                                                    <div className="mt-2 text-sm text-gray-500">
                                                        Uploading media, please wait...
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="space-y-6 mb-6">
                                            {componentsState.map((component, index) => (
                                                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                                                    <div className="flex items-center mb-3">
                                                        <span className="bg-blue-500 text-white px-3 py-1 rounded text-sm">{component.type}</span>
                                                        <span className="ml-3 text-gray-600">{component.inputFieldCount} variable(s)</span>
                                                    </div>

                                                    <div className="text-gray-700 mb-4 p-3 bg-white rounded border">
                                                        <pre className="whitespace-pre-wrap">{component.text}</pre>
                                                    </div>

                                                    <div className="space-y-3">
                                                        {component.variables.map((variable, vIndex) => (
                                                            <div key={vIndex} className="flex flex-col">
                                                                <label className="mb-1 text-sm font-medium text-gray-700">
                                                                    {variable.fullVar}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    placeholder={`Enter value for ${variable.key}`}
                                                                    value={inputValues[`${component.type}_${variable.key}`] || ''}
                                                                    onChange={(e) => handleVariablesInputChange(component.type, variable.key, e.target.value)}
                                                                    className="border rounded p-2 w-full"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="mt-4 text-sm text-gray-600">
                                                        <div className="font-semibold">Preview:</div>
                                                        <div className="mt-1">
                                                            {component.text.replace(/{{\s*([^{}]+)\s*}}/g, (match, key) => {
                                                                return inputValues[`${component.type}_${key}`] || match;
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex justify-end space-x-3">
                                            <button
                                                onClick={() => handleComponentModalClose()}
                                                className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            {uploadingMedia ? (
                                                <button
                                                    disabled
                                                    className={`px-6 py-2 text-white rounded-full bg-gray-400 cursor-not-allowed`}
                                                >
                                                    Uploading...
                                                </button>
                                            ) : (
                                                <>
                                                    {sendLoading ? (
                                                        <button
                                                            disabled
                                                            className={`px-6 py-2 text-white rounded-full bg-gray-400 cursor-not-allowed`}
                                                        >
                                                            Sending...
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleTemplateSubmit()}
                                                            className={`px-6 py-2 text-white rounded-full bg-cyan-600  ${!selectedPhoneNumberInfo?.numberId || uploadingMedia ? 'bg-gray-400 cursor-not-allowed' : 'bg-cyan-600 hover:bg-cyan-700'}`}
                                                            disabled={!selectedPhoneNumberInfo?.numberId || uploadingMedia}
                                                        >
                                                            Send
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
                {action === 'Send Email' && (
                    <div className="p-6">
                        {/* Recipients Section */}
                        <div className="flex flex-col">
                            <label className="mb-2 text-sm font-medium text-gray-700">Recipients</label>

                            {recipients.length > 0 ? (
                                <>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {recipients.map((recipient, index) => (
                                            <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                                {recipient.email}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="text-xs text-gray-500 mb-4">
                                        {recipients.length} of {selectedIds.length} selected leads have email addresses
                                    </div>
                                </>
                            ) : (
                                <div>
                                    {/* <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm text-yellow-700">
                                                    No email addresses found for selected leads.
                                                    {selectedIds.length > 1 ? ' These leads' : ' This lead'} won't receive emails.
                                                </p>
                                            </div>
                                        </div>
                                    </div> */}
                                </div>
                            )}

                            {/* Subject Section */}
                            <label className="mb-2 text-sm font-medium text-gray-700">Subject</label>
                            <input
                                type="text"
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                className="border rounded-lg p-2 w-full mb-2"
                                placeholder="Enter email subject"
                                maxLength={100}
                                style={{ border: '1px solid #3483BE' }}
                            />
                            <div className="flex items-center space-x-3 mb-4">
                                <span className={`text-xs px-2 py-1 rounded-full ${emailSubject.length === 100 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {emailSubject.length} / 100
                                </span>
                            </div>

                            {/* Body Section */}
                            <label className="mb-2 text-sm font-medium text-gray-700">Body</label>
                            <textarea
                                value={emailBody}
                                onChange={(e) => setEmailBody(e.target.value)}
                                className="border rounded-lg p-2 w-full mb-2"
                                placeholder="Enter email body"
                                rows={10}
                                maxLength={1500}
                                style={{ resize: 'none', border: '1px solid #3483BE' }}
                            ></textarea>
                            <div className="flex items-center space-x-3 mb-4">
                                <span className={`text-xs px-2 py-1 rounded-full ${emailBody.length === 500 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                                    {emailBody.length} / 1500
                                </span>
                                <div className="flex items-center cursor-pointer" title="Attach files (max 20MB each)">
                                    {/* Hidden file input */}
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelection}
                                        className="hidden"
                                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt"
                                        multiple
                                    />
                                    <div
                                        onClick={handleIconClick}
                                        className="cursor-pointer p-2 hover:bg-gray-100 rounded-full"
                                    >
                                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Attachments Section */}
                        {attachments.length > 0 && (
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Attachments ({attachments.length})
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                    {attachments.map((attachment, index) => (
                                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border">
                                            <div className="flex items-center truncate">
                                                <svg className="flex-shrink-0 h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <span className="text-sm truncate text-gray-700" title={attachment.filename}>
                                                    {attachment.filename}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => handleAttachmentRemove(index)}
                                                className="ml-2 text-red-500 hover:text-red-700"
                                                title="Remove attachment"
                                            >
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3 mt-6">

                            <button
                                onClick={() => setIsTemplatesOpen(true)}
                                className={`px-6 py-2 text-white rounded-full bg-rose-600 hover:bg-rose-700 flex items-center`}
                            >
                                Use Templates
                                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="#FFFFFF"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                            </button>

                            {sendLoading ? (
                                <button
                                    disabled
                                    className="px-6 py-2 text-white rounded-full bg-gray-400 cursor-not-allowed flex items-center"
                                >
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                </button>
                            ) : (
                                <button
                                    className={`px-6 py-2 text-white rounded-full flex items-center ${!emailSubject || !emailBody || recipients.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#F9536B] hover:to-[#FF7B6F]'}`}
                                    disabled={!emailSubject || !emailBody || recipients.length === 0}
                                    onClick={() => handleSendEmail()}
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Send Email
                                </button>
                            )}

                            <button
                                onClick={() => onClose('')}
                                className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>

                        {/* Templates Modal */}
                        {isTemplatesOpen && (
                            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                                <div className="bg-white rounded-3xl w-[80%] xl:w-[50%] max-h-[90vh] overflow-y-auto">
                                    {/* Filters */}
                                    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                                        <div className="flex flex-col md:flex-row gap-4">
                                            <div className="flex-1">
                                                <div className="relative">
                                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search templates..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                            <select
                                                value={selectedCategory}
                                                onChange={(e) => setSelectedCategory(e.target.value)}
                                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                {categories.map(category => (
                                                    <option key={category} value={category}>{category}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Templates List */}
                                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                                        <div className="px-6 py-4 border-b border-gray-200">
                                            <h2 className="text-xl font-semibold text-gray-900">
                                                Templates ({filteredTemplates.length})
                                            </h2>
                                        </div>

                                        {filteredTemplates.length === 0 ? (
                                            <div className="p-12 text-center">
                                                <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
                                                <p className="text-gray-600">
                                                    {searchTerm || selectedCategory !== 'All'
                                                        ? 'Try adjusting your search or filter criteria'
                                                        : 'Create your first email template to get started'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-200">
                                                {filteredTemplates.map(template => (
                                                    <div key={template.id} className="p-6 hover:bg-gray-50 transition-colors"
                                                        onClick={() => handleTemplateSelect(template)}
                                                    >
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-3 mb-2">
                                                                    <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                                                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                                        {template.category}
                                                                    </span>
                                                                </div>
                                                                <p className="text-gray-600 mb-2 font-medium">{template.subject}</p>
                                                                <div className="text-sm text-gray-500 mb-3">
                                                                    {template.content.substring(0, 150)}
                                                                    {template.content.length > 150 && '...'}
                                                                </div>
                                                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                                                    <div className="flex items-center gap-1">
                                                                        <Calendar className="h-4 w-4" />
                                                                        Created: {formatDate(template.createdAt)}
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <Calendar className="h-4 w-4" />
                                                                        Modified: {formatDate(template.lastModified)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-end space-x-3 p-6">
                                        <button
                                            onClick={() => setIsTemplatesOpen(false)}
                                            className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                        >
                                            Back
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                )}
            </div>
        </div>
    )
}

export default ActionModals